import React, { useEffect } from 'react';

import styled from 'styled-components';

import { Color } from '@assets/constants';
import { icons } from '@assets/icons';
import { CartProductCard } from '@features/cart/CartProductCard';
import { RemainingForFreeShipping } from '@features/checkout/RemainingForFreeShipping';
import {
  useAppDispatch,
  useCustomNavigate,
  useModalClose,
  useScreenSize
} from '@hooks';
import { Product } from '@models/product';
import { ScreenSize } from '@models/screen';
import { useActiveDiscounts, useProductsById, useShipping } from '@queries';
import { cartActions, LocalItem } from '@store';
import { getLocalCartItems } from '@store';
import { ActivityIndicator } from '@ui/ActivityIndicator';
import { Button } from '@ui/Button';
import { Divider } from '@ui/Divider';
import { Modal, ModalEnterAnimation, ModalExitAnimation } from '@ui/Modal';
import { bgnToEur } from '@utils/bgnToEur';

import { CartProduct } from '../../../domain/mappers/cartProductMapper';
import { addPriceToItems } from './utils';

interface Props {
  showCart: boolean;
  onCloseCart: () => void;
  cartItems: CartProduct[];
}

export const Cart = ({ showCart, onCloseCart, cartItems }: Props) => {
  const dispatch = useAppDispatch();
  const navigate = useCustomNavigate();
  const { closing, handleClose } = useModalClose(onCloseCart);
  const screenSize = useScreenSize();
  const { data: activeDiscounts, isPending: isFetchingDiscounts } =
    useActiveDiscounts();
  const { data: shipping, isPending: isFetchingShipping } = useShipping();

  const localCartItems: LocalItem[] = getLocalCartItems();
  const { products, isFetchingProducts } = useProductsById(localCartItems);

  const isSmallScreen = screenSize === ScreenSize.SMALL;

  const setItemsToCart = async () => {
    if (isFetchingProducts) return;

    const cartItems = await addPriceToItems(
      localCartItems,
      products as Product[],
      activeDiscounts
    );

    dispatch(cartActions.fillCart(cartItems));
  };

  useEffect(() => {
    setItemsToCart();
  }, [showCart, isFetchingProducts]);

  const cartPrice = Number(
    cartItems
      .reduce((acc, product) => {
        return acc + product.price;
      }, 0)
      .toFixed(2)
  );
  const isFreeShipping = cartPrice >= shipping.minimumAmount;
  const shippingPrice =
    isFreeShipping || cartItems.length === 0 ? 0 : shipping.shippingCost;
  const totalPrice = cartPrice + shippingPrice;

  const isLoading = isFetchingDiscounts || isFetchingShipping;

  return (
    <>
      {showCart && (
        <Modal
          onClose={handleClose}
          enterAnimation={ModalEnterAnimation.SLIDE_LEFT}
          exitAnimation={ModalExitAnimation.SLIDE_RIGHT}
          closing={closing}
          backButton
          additionalStyles={`
            border-top-right-radius: 0;
            border-bottom-right-radius: 0;
            ${
              !isSmallScreen
                ? `
                height: 100vh;
                width: 420px;
              `
                : `
                width: 100%;
              `
            }`}
        >
          <Container>
            <CartHeader>
              <icons.FaShoppingBag size={24} />
              Кошница
            </CartHeader>
            {isLoading ? (
              <ActivityIndicatorWrapper>
                <ActivityIndicator color={Color.ACCENT} size={100} />
              </ActivityIndicatorWrapper>
            ) : (
              <>
                <CartItemsContainer disableScroll={cartItems.length < 2}>
                  {cartItems.length > 0 &&
                    cartItems.map((product, index) => (
                      <CartProductCard
                        product={product}
                        removeButton
                        key={`${product.id}-${index}`}
                      />
                    ))}
                </CartItemsContainer>
                <CartFooter>
                  <CartPriceContainer>
                    Тениски:
                    <p>
                      {cartPrice.toFixed(2)}лв / €{bgnToEur(cartPrice)}
                    </p>
                  </CartPriceContainer>
                  <CartPriceContainer>
                    Доставка:
                    <p>
                      {shippingPrice.toFixed(2)}лв / €{bgnToEur(shippingPrice)}
                    </p>
                  </CartPriceContainer>
                  {!isFreeShipping && (
                    <RemainingForFreeShipping
                      totalPrice={cartPrice}
                      minimumAmount={shipping.minimumAmount}
                    />
                  )}
                  <Divider additionalStyles={'margin: 10px 0;'} />
                  <CartPriceContainer>
                    Общо:{' '}
                    <p>
                      {totalPrice.toFixed(2)}лв / €{bgnToEur(totalPrice)}
                    </p>
                  </CartPriceContainer>
                  <Button
                    label={'Купи'}
                    onClick={() => {
                      navigate('/checkout');
                      handleClose();
                    }}
                    disabled={cartItems.length === 0}
                  ></Button>
                </CartFooter>
              </>
            )}
          </Container>
        </Modal>
      )}
    </>
  );
};

const ActivityIndicatorWrapper = styled.div`
  display: flex;
  justify-content: center;
  height: 100%;
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: calc(100vh - 72px);
  width: 100%;
`;

const CartHeader = styled.h1`
  position: absolute;
  display: flex;
  gap: 10px;
  color: ${Color.DARK_GRAY};
  background-color: transparent;
  font-size: 1.25rem;
  font-weight: 500;
  top: 40px;
  right: 50%;
  transform: translateX(50%);
  // hacky way to prevent background scrolling on iOS
  touch-action: none;
`;

const CartItemsContainer = styled.div<{ disableScroll: boolean }>`
  padding: 1rem;
  display: flex;
  height: 100%;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  margin-top: 1rem;
  gap: 1rem;
  padding-bottom: 400px;
  overflow-y: auto;
  // hacky way to fix background scrolling on iOS
  ${({ disableScroll }) => disableScroll && `touch-action: none;`}
`;

const CartFooter = styled.div`
  background-color: ${Color.WHITE};
  z-index: 12;
  bottom: 0;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 1.5rem;
  border-top-right-radius: 2.5rem;
  border-top-left-radius: 2.5rem;
  box-shadow: 0px -2px 10px rgba(0, 0, 0, 0.15);
  touch-action: none;

  @media (max-width: 768px) {
    position: fixed;
  }
`;

const CartPriceContainer = styled.div`
  display: flex;
  margin-bottom: 10px;
  p {
    margin-left: auto;
    font-weight: 600;
    color: ${Color.BLACK};
  }
`;
